import React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useModals } from "common/utils/modals";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import { useStoreSyncedWithLocalStorage } from "common/store/store";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import ListItemText from "@mui/material/ListItemText";
import {
  DAY,
  formatDayOfWeek,
  formatTimeOfDay,
  prettyFormatDay,
  shortPrettyFormatDay,
  startOfDay
} from "common/utils/time";
import ListSubheader from "@mui/material/ListSubheader";
import orderBy from "lodash/orderBy";
import maxBy from "lodash/maxBy";
import { LoadingButton } from "common/components/LoadingButton";
import BackgroundImage from "common/assets/images/landing-hero-vertical-without-text-logo.svg";
import LogoWithText from "common/assets/images/mobilic-logo-white-with-text.svg";
import { shouldDisplayEmployeeSocialImpactSurveyOnMainPage } from "common/utils/surveys";
import { usePageTitle } from "../../common/UsePageTitle";
import { useNewMissionFunnel } from "../../common/hooks/useNewMissionFunnel";
import { useHolidays } from "../../common/useHolidays";
import { WarningBreaks } from "../components/WarningBreaks";
import { useEnoughBreak } from "../../common/useEnoughBreak";
import Stack from "@mui/material/Stack";
import { Notifications } from "../components/notifications/Notifications";
import { MobilicHeader } from "../../common/Header";
import { Button } from "@codegouvfr/react-dsfr/Button";
import { fr } from "@codegouvfr/react-dsfr";
import { WarningRealTimeEntry } from "../components/WarningRealTimeEntry";
import { useLastMissionEditType } from "../../common/useLastMissionEditType";
import PushNotificationBanner from "common/components/PushNotificationBanner";

const MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY = 5;

const useStyles = makeStyles(theme => ({
  outer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    background: `url(${BackgroundImage}) 50%`,
    backgroundSize: "cover",
    position: "relative"
  },
  missionsToValidateList: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "justify",
    marginTop: theme.spacing(2),
    borderRadius: "24px 24px 0 0"
  },
  listHeader: {
    fontWeight: "bold",
    fontSize: "1.125rem"
  },
  listSubheader: {
    fontSize: "1rem",
    fontWeight: 500,
    display: "block",
    lineHeight: "1.25rem",
    color: theme.palette.grey[600]
  },
  missionDay: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: 50,
    width: 65,
    color: theme.palette.text.primary,
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.grey[200]
  },
  missionName: {
    color: theme.palette.primary.main,
    fontWeight: "bold"
  },
  ellipsis: {
    color: theme.palette.grey[600]
  },
  subButton: {
    color: theme.palette.primary.contrastText,
    "&:hover": {
      color: theme.palette.primary.main
    }
  },
  startButton: {
    backgroundColor: "white",
    color: fr.colors.decisions.text.actionHigh.blueFrance.default,
    "&:hover": {
      color: "white"
    },
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6)
  },
  histoButton: {
    marginTop: theme.spacing(2),
    color: "white",
    "&:hover": {
      color: fr.colors.decisions.text.actionHigh.blueFrance.default
    },
    textDecoration: "underline",
    textUnderlineOffset: "6px"
  }
}));

export function BeforeWork({ openHistory, missions }) {
  usePageTitle("Saisie Temps - Mobilic");
  const modals = useModals();
  const store = useStoreSyncedWithLocalStorage();
  const { openHolidaysModal } = useHolidays();
  const { hasEnoughBreak } = useEnoughBreak();

  const companies = store.companies();
  const userId = store.userId();
  const userInfo = store.userInfo();

  const { onEnterNewMissionFunnel } = useNewMissionFunnel(); 
  const onEnterNewHolidayFunnel = () => openHolidaysModal();

  const classes = useStyles();

  const currentTime = startOfDay(new Date(Date.now()));

  const missionsInHistory = missions.filter(m => m.isComplete && m.ended);
  const lastMission = maxBy(missionsInHistory, "endTime");
  const { hasBeenEditedByEmployee } = useLastMissionEditType(lastMission);
  const shouldShowRealTimeEntryWarning =
    hasBeenEditedByEmployee &&
    lastMission &&
    Date.now() / 1000 - lastMission.endTime < DAY;
  const shouldShowBreaksWarning =
    process.env.REACT_APP_ENOUGH_BREAK_BANNER === "1" &&
    !hasEnoughBreak &&
    lastMission &&
    Date.now() / 1000 - lastMission.endTime < DAY;
  const nonValidatedMissions = orderBy(
    missionsInHistory.filter(
      m =>
        !m.isDeleted &&
        !m.validation &&
        !m.adminValidation &&
        m.startTime >= currentTime - DAY * 31
    ),
    ["startTime"],
    ["desc"]
  );

  React.useEffect(() => {
    if (
      shouldDisplayEmployeeSocialImpactSurveyOnMainPage(userInfo, companies)
    ) {
      modals.open("typeformModal", {
        typeformId: process.env.REACT_APP_SURVEY_EMPLOYEE_SOCIAL_IMPACT,
        userId: userId
      });
    }
  }, []);

  return (
    <Container maxWidth={false} className={classes.outer} disableGutters>
      <MobilicHeader forceMobile />
      <PushNotificationBanner />
      <Box sx={{ display: "grid", alignItems: "start" }}>
        {shouldShowBreaksWarning && (
          <Box sx={{ gridArea: "1 / 1" }}>
            <WarningBreaks dismissKey={`${userId}-${lastMission?.id}`} />
          </Box>
        )}
        {shouldShowRealTimeEntryWarning && (
          <Box sx={{ gridArea: "1 / 1", zIndex: 1 }}>
            <WarningRealTimeEntry
              dismissKey={`${userId}-${lastMission?.id}`}
            />
          </Box>
        )}
      </Box>
      <Stack
        direction="column"
        alignItems="center"
        height="100%"
        pb={10}
        justifyContent="space-between"
      >
        <Box flexGrow={1} pt={10}>
          <img alt="mobilic-logo-text" src={LogoWithText} width={117} />
        </Box>
        <Stack direction="column" rowGap={2} alignItems="center">
          <Button
            size="large"
            onClick={onEnterNewMissionFunnel}
            className={classes.startButton}
          >
            Commencer une mission
          </Button>
          <LoadingButton
            className={classes.subButton}
            onClick={() => {
              onEnterNewHolidayFunnel();
            }}
            priority="tertiary no outline"
            iconId="fr-icon-calendar-2-fill"
            iconPosition="left"
          >
            Renseigner une indisponibilité
          </LoadingButton>
          <Button
            onClick={() => {
              openHistory();
            }}
            priority="tertiary no outline"
            iconId="fr-icon-arrow-right-line"
            iconPosition="right"
            className={classes.histoButton}
          >
            Voir mon historique
          </Button>
        </Stack>
      </Stack>
      {nonValidatedMissions.length > 0 && (
        <Box className={classes.missionsToValidateList}>
          <List
            subheader={
              <ListSubheader
                component="div"
                color="inherit"
                className={classes.listHeader}
              >
                Missions à valider
                <span className={classes.listSubheader}>
                  Vérifiez les informations saisies par vous-même ou par vos
                  coéquipiers
                </span>
              </ListSubheader>
            }
          >
            {nonValidatedMissions
              .slice(0, MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY)
              .map(m => (
                <MissionItem key={m.id} mission={m} openHistory={openHistory} />
              ))}
            {nonValidatedMissions.length >
              MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY && (
              <ListItem className={classes.ellipsis}>
                <Typography>
                  +{" "}
                  {nonValidatedMissions.length -
                    MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY}{" "}
                  autre
                  {nonValidatedMissions.length -
                    MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY >
                  1
                    ? "s"
                    : ""}{" "}
                  mission
                  {nonValidatedMissions.length -
                    MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY >
                  1
                    ? "s"
                    : ""}{" "}
                  non validée
                  {nonValidatedMissions.length -
                    MAX_NON_VALIDATED_MISSIONS_TO_DISPLAY >
                  1
                    ? "s"
                    : ""}
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      )}
      <Notifications openHistory={openHistory} />
    </Container>
  );
}

function MissionItem({ mission, openHistory }) {
  const classes = useStyles();

  return (
    <ListItem button onClick={() => openHistory(mission.id)}>
      <ListItemAvatar>
        <Avatar variant="rounded" className={classes.missionDay}>
          <Typography style={{ textTransform: "uppercase" }}>
            {formatDayOfWeek(mission.startTime)}
          </Typography>
          <Typography className="bold">
            {shortPrettyFormatDay(mission.startTime)}
          </Typography>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={`Mission ${mission.name ||
          `du ${prettyFormatDay(mission.startTime)}`}`}
        primaryTypographyProps={{ className: classes.missionName }}
        secondary={`${formatTimeOfDay(mission.startTime)} - ${formatTimeOfDay(
          mission.endTime
        )}`}
      />
    </ListItem>
  );
}
